import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'

/**
 * Your private note on a lesson.
 *
 * Autosaves after a pause rather than on every keystroke — debouncing is the
 * difference between one write and forty. Explicit save state ("Saved") matters
 * more than it looks: without it, people retype things they already wrote
 * because nothing told them it stuck.
 */
export default function NoteEditor({ lessonId }: { lessonId: string }) {
  const [body, setBody] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'saving' | 'saved' | 'error'>('loading')
  const [loaded, setLoaded] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let alive = true
    setStatus('loading')
    setLoaded(false)
    api<{ note: { body: string } | null }>(`/notes/${lessonId}`)
      .then((d) => {
        if (!alive) return
        setBody(d.note?.body ?? '')
        setStatus('idle')
        setLoaded(true)
      })
      .catch(() => {
        if (!alive) return
        setStatus('idle')
        setLoaded(true)
      })
    return () => {
      alive = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [lessonId])

  function onChange(value: string) {
    setBody(value)
    if (!loaded) return

    if (timer.current) clearTimeout(timer.current)
    setStatus('saving')
    timer.current = setTimeout(() => {
      api('/notes', { method: 'PUT', body: { lessonId, body: value } })
        .then(() => setStatus('saved'))
        .catch(() => setStatus('error'))
    }, 800)
  }

  return (
    <div className="note-editor">
      <div className="note-head">
        <h4>Your notes</h4>
        <span className="dim note-status">
          {status === 'loading' && 'loading…'}
          {status === 'saving' && 'saving…'}
          {status === 'saved' && 'saved ✓'}
          {status === 'error' && <span className="note-error">could not save</span>}
        </span>
      </div>
      <textarea
        value={body}
        onChange={(e) => onChange(e.target.value)}
        rows={5}
        placeholder="Answer the lab here, or write down the thing you want to remember. Only you can see this."
        aria-label="Your notes on this lesson"
      />
      <p className="dim note-hint">
        Autosaves. Writing a note also completes the daily <strong>build</strong> quest.
      </p>
    </div>
  )
}
